import { ReactNode } from 'react'
import { Box, Button, Divider, Typography } from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Deposit, Fund, TotalUnitsChange } from '../../api/types'
import { navTotalOf } from '../../calc/preview'
import { InfoHint } from '../../components/InfoHint'
import { SectionCard } from '../../components/SectionCard'
import { TotalUnitsHistory } from './TotalUnitsHistory'
import { formatCzk, formatDate, formatUnits } from './format'

interface FundOverviewCardProps {
  fund: Fund
  /** PAID vklady fondu čekající na přidělení (fond-scoped). */
  queueDeposits: Deposit[]
  /** Ruční opravu celkových jednotek smí otevřít jen CEO; ostatní vidí jen číslo. */
  canEditTotalUnits?: boolean
  onEditTotalUnits?: () => void
  /** Historie celku jednotek: všechny pohyby (nejnovější první). */
  unitsHistory?: TotalUnitsChange[]
  unitsHistoryError?: string | null
}

/** České skloňování „vklad" pro počet ve frontě (1 vklad / 2–4 vklady / 5+ vkladů). */
function vkladyLabel(count: number): string {
  if (count === 1) return '1 vklad'
  if (count >= 2 && count <= 4) return `${count} vklady`
  return `${count} vkladů`
}

/** Přehled fondu před přepočtem: kumulativní jednotky, poslední ocenění, fronta. */
export function FundOverviewCard({
  fund,
  queueDeposits,
  canEditTotalUnits = false,
  onEditTotalUnits,
  unitsHistory = [],
  unitsHistoryError = null,
}: FundOverviewCardProps) {
  // Σ čekajících vkladů přes navTotalOf — žádný ruční float reduce (kontrakt calc).
  const queueTotal = navTotalOf(
    queueDeposits.map((d) => ({ amount: d.amountReceived ?? d.amount }))
  )

  return (
    <SectionCard
      title="Přehled fondu"
      info={
        <InfoHint
          title="Odkud jsou čísla"
          lines={[
            'Celkové jednotky = suma všech přidělených jednotek fondu (roste potvrzenými přepočty, klesá stržením success fee).',
            'Cena jednotky = z posledního ocenění (NAV / celkové jednotky), snižuje ji i měsíční management fee.',
            'Celkové jednotky lze ručně opravit (jen CEO) — zanesení jednotek upsaných mimo modul. Mění cenu jednotky všem klientům fondu, proto povinný důvod a audit.',
          ]}
          sx={{ color: '#fff' }}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <OverviewRow
          label="Celkem jednotek (kumulativně)"
          value={
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatUnits(fund.totalUnits)}
              </Typography>
              {canEditTotalUnits && onEditTotalUnits && (
                <Button
                  size="small"
                  startIcon={<EditOutlinedIcon fontSize="small" />}
                  onClick={onEditTotalUnits}
                  sx={{ mt: 0.25 }}
                >
                  Opravit ručně
                </Button>
              )}
            </Box>
          }
        />

        <OverviewRow
          label="Poslední ocenění"
          value={
            fund.lastValuation ? (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(fund.lastValuation.date)}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  NAV {formatCzk(fund.lastValuation.navTotal)} · cena jednotky{' '}
                  {formatUnits(fund.lastValuation.unitPrice)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Zatím žádné ocenění
              </Typography>
            )
          }
        />

        <OverviewRow
          label="Ve frontě čeká"
          value={
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {vkladyLabel(queueDeposits.length)}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {formatCzk(queueTotal)}
              </Typography>
            </Box>
          }
        />

        <Divider />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Historie celku jednotek
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <TotalUnitsHistory changes={unitsHistory} error={unitsHistoryError} />
          </Box>
        </Box>
      </Box>
    </SectionCard>
  )
}

function OverviewRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {value}
    </Box>
  )
}
